import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApi, type PropertyGroupResponse } from '@/shared/api';
import { OBJECT_SCOPE_STORAGE, readScope, useScopeStorageKey, writeScope } from './scopeStorage';

interface ObjectScopeValue {
  groups: PropertyGroupResponse[];
  isLoading: boolean;
  group: PropertyGroupResponse | null;
  groupId: string | null;
  propertyIds: string[] | null;
  setGroupId: (id: string | null) => void;
  /** Die Objektliste kam nicht an – ohne das läse sich ein Fehler wie „keine Objekte“. */
  isError: boolean;
  /** Ohne Netz pausiert die erste Abfrage: weder lädt sie noch scheitert sie. */
  isOffline: boolean;
  isFetching: boolean;
  error: unknown;
  /** Die gespeicherte Auswahl, auch wenn sie gerade nicht greift (Liste fehlt). */
  savedGroupId: string | null;
  retry: () => void;
  /** Ruhend ohne Objekt-Leserecht: keine Abfrage, keine Auswahl. */
  active: boolean;
}

const ObjectScopeContext = createContext<ObjectScopeValue | null>(null);

export function ObjectScopeProvider({
  children,
  enabled = true,
}: {
  children: ReactNode;
  /** Aus: der Kontext ruht; wer ihn braucht, bekommt „kein Objekt“ statt eines Absturzes. */
  enabled?: boolean;
}) {
  const api = useApi();
  const groups = useQuery({
    // Ruhend auf eigenem Schlüssel: sonst hielte der Beobachter Objektdaten einer früheren Sitzung.
    queryKey: enabled ? ['property-groups'] : ['property-groups', 'idle'],
    queryFn: api.propertyGroups.list,
    staleTime: 60_000,
    enabled,
  });
  const data = enabled ? groups.data : undefined;
  const { refetch } = groups;
  const storageKey = useScopeStorageKey(OBJECT_SCOPE_STORAGE);
  const [groupId, setStoredGroupId] = useState<string | null>(() => readScope(storageKey));
  const group = data?.find((item) => item.id === groupId) ?? null;
  const setGroupId = useCallback(
    (id: string | null) => {
      if (!enabled) return;
      setStoredGroupId(id);
      writeScope(storageKey, id);
    },
    [enabled, storageKey],
  );
  useEffect(() => {
    if (groupId && data && !group) setGroupId(null);
  }, [groupId, group, data, setGroupId]);
  const value = useMemo(
    () => ({
      groups: data ?? [],
      isLoading: enabled && groups.isLoading,
      group,
      groupId: group?.id ?? null,
      propertyIds: group?.apartments.map((apartment) => apartment.id) ?? null,
      setGroupId,
      isError: enabled && groups.isError,
      isOffline: enabled && !data && groups.fetchStatus === 'paused',
      isFetching: enabled && groups.isFetching,
      error: enabled ? groups.error : null,
      savedGroupId: enabled ? groupId : null,
      // refetch lädt auch eine abgeschaltete Abfrage – ruhend darf es nichts anfragen.
      retry: () => {
        if (enabled) void refetch();
      },
      active: enabled,
    }),
    [
      data,
      enabled,
      groups.isLoading,
      groups.isError,
      groups.fetchStatus,
      groups.isFetching,
      groups.error,
      groupId,
      group,
      setGroupId,
      refetch,
    ],
  );
  return <ObjectScopeContext.Provider value={value}>{children}</ObjectScopeContext.Provider>;
}

export function useObjectScope() {
  const value = useContext(ObjectScopeContext);
  if (!value) throw new Error('useObjectScope must be inside ObjectScopeProvider');
  return value;
}

/** Nur ein aktiver Kontext zählt: ruhend lädt etwa die Reinigung ihre eigenen Bereiche. */
export function useOptionalObjectScope() {
  const value = useContext(ObjectScopeContext);
  return value?.active ? value : null;
}

export function filterByObjectScope<T extends { propertyId?: string | null }>(
  items: T[],
  propertyIds: string[] | null,
): T[] {
  return propertyIds === null
    ? items
    : items.filter((item) => item.propertyId && propertyIds.includes(item.propertyId));
}
